import ContentBlock from '../../ContentBlock';
import { NarrativeDisclaimer } from '../../NarrativeDisclaimer';
import { NarrativeSponsoredNotice } from '../../NarrativeSponsoredNotice';
import loadable from '@loadable/component';
import type { Narrative } from '@/pages/Narrative/hooks/useNarrative';
import { memo } from 'react';
import { NarrativeViewTabs } from './NarrativeViewTabs';
import { NarrativeUpdates } from '../../NarrativeUpdates';
import { useFlag } from '@simplywallst/unleash';
import { NarrativeUpdateCarousel } from '../../NarrativeUpdateCarousel';

const Rating = loadable(() => import('../../Rating'));
const NarrativeComment = loadable(() => import('../../NarrativeComment'), {
  resolveComponent: (c) => c.NarrativeComment,
});
const Announcement = loadable(() => import('../../Announcement'), {
  resolveComponent: (c) => c.Announcement,
});
const NarrativesRelated = loadable(() => import('../../NarrativesRelated'), {
  resolveComponent: (c) => c.NarrativesRelated,
});

type Props = {
  narrative: Narrative;
  commentRef: React.RefObject<HTMLDivElement>;
};

export const NarrativeMainContentBase = ({ narrative, commentRef }: Props) => {
  const {
    id: narrativeId,
    company,
    slug,
    contentBlocks,
    userHoldsPosition,
    announcements,
    commentKey,
    owner,
    isSponsored,
    publishedUpdates,
  } = narrative;

  const { enabled } = useFlag('mono-narrative-updates-view');

  if (!company) throw new Error("Narrative's company is missing");

  const announcement = announcements?.at(-1);
  const latestUpdate = publishedUpdates[0];
  return (
    <NarrativeViewTabs updateCount={publishedUpdates.length}>
      {(tab) => {
        if (tab === 'updates')
          return (
            <article className="grid auto-rows-max gap-x2">
              <NarrativeUpdates updates={publishedUpdates} />
            </article>
          );

        return (
          <article className="grid auto-rows-max gap-x2">
            {enabled && latestUpdate && (
              <NarrativeUpdateCarousel updates={publishedUpdates} />
            )}
            {announcement && !enabled && (
              <Announcement slug={slug} announcement={announcement} />
            )}
            {contentBlocks.map(({ contentHtml, title }, index) => (
              <ContentBlock
                key={`${title}-${index}`}
                contentHtml={contentHtml}
              />
            ))}
            {company && (
              <Rating
                companyId={company.id}
                narrativeId={narrativeId}
                uniqueSymbol={company.uniqueSymbol}
              />
            )}
            <NarrativeComment commentKey={commentKey} ref={commentRef} />
            <NarrativeDisclaimer
              userHoldsPosition={userHoldsPosition}
              authorDisplayName={owner?.displayName}
              companyTicker={company.uniqueSymbol}
              disclaimerType={owner?.disclaimerType}
            />
            <NarrativeSponsoredNotice
              companyName={company.name}
              isSponsored={isSponsored}
            />
            <NarrativesRelated slug={slug} />
          </article>
        );
      }}
    </NarrativeViewTabs>
  );
};

export const NarrativeMainContent = memo(NarrativeMainContentBase);
